import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Globe } from 'lucide-react';
import type { CountryToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useCountriesList, useCountryClassifications, useCountryMutations } from '~/hooks/use-countries';
import { tokenToForm, type CountryFormValues } from '~/lib/schemas/country';
import { CountryEditDialog } from './country-edit-dialog';

/** Countries CRUD surface — parity with the legacy admin grid (Name/ISO-2/Classification/Visible/Sort). */
export function CountriesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useCountriesList();
	const { data: classifications = [] } = useCountryClassifications();
	const { remove } = useCountryMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<CountryFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<CountryToken | null>(null);

	const classMap = useMemo(() => new Map(classifications.map(c => [c.id, c.nm])), [classifications]);
	const classOf = (c: CountryToken) => classMap.get(c.cls ?? -1) ?? '';

	const columns = useMemo<SystemGridColumn<CountryToken>[]>(
		() => [
			{
				key: 'name',
				header: t('system.countries.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: c => c.nm,
				exportValue: c => c.nm,
				render: c => <span className="truncate text-sm font-medium text-foreground">{c.nm}</span>
			},
			{
				key: 'iso2',
				header: t('system.countries.columns.iso2'),
				width: '90px',
				sortValue: c => c.iso2,
				exportValue: c => c.iso2,
				render: c => <span className="font-mono text-xs text-foreground-muted">{c.iso2}</span>
			},
			{
				key: 'classification',
				header: t('system.countries.columns.classification'),
				width: '160px',
				sortValue: c => classOf(c),
				exportValue: c => classOf(c),
				render: c => <span className="truncate text-sm text-foreground-muted">{classOf(c) || '—'}</span>
			},
			{
				key: 'erpCode',
				header: t('system.fields.erpCode'),
				width: '120px',
				defaultVisible: false,
				sortValue: c => c.erpcd ?? '',
				exportValue: c => c.erpcd ?? '',
				render: c => <span className="font-mono text-xs text-foreground-muted">{c.erpcd || '—'}</span>
			},
			{
				key: 'visible',
				header: t('system.countries.columns.visible'),
				width: '80px',
				align: 'center',
				sortValue: c => (c.iv ? 1 : 0),
				exportValue: c => (c.iv ? t('common.yes') : t('common.no')),
				render: c => (c.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('system.countries.columns.sort'),
				width: '70px',
				align: 'right',
				sortValue: c => c.si ?? 0,
				exportValue: c => c.si ?? 0,
				render: c => <span className="text-sm tabular-nums text-foreground-muted">{c.si}</span>
			}
		],
		// classMap feeds classOf; recompute the columns when it resolves.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, classMap]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<CountryToken>
				gridId="countries"
				title={t('system.countries.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={c => c.id ?? c.iso2}
				columns={columns}
				searchPredicate={(c, q) => `${c.nm ?? ''} ${c.iso2 ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Globe size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.countries.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={c => {
					setEditing(tokenToForm(c));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="countries"
			/>

			<CountryEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} classifications={classifications} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.countries.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.countries.deleteBody', { name: deleteRow?.nm ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
